import { useCallback, useEffect, useState } from 'react';
import { FieldValues, useFormState } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import useCreateInquiry, {
  UseCreateInquiryProps,
} from 'dashboard/apps/InquiryTemplateEditor/hooks/useCreateInquiry';
import useLoadInquiryTemplateVersion from 'dashboard/apps/InquiryTemplateEditor/hooks/useLoadInquiryTemplateVersion';
import CreateInquiryFieldsPrefill from 'dashboard/components/CreateInquiry/CreateInquiryFieldsPrefill';
import CreateInquiryGeneralInfo from 'dashboard/components/CreateInquiry/CreateInquiryGeneralInfo';
import CreateInquiryGeneralInfoForAccount from 'dashboard/components/CreateInquiry/CreateInquiryGeneralInfoForAccount';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import PostInquiryCreateModal from 'dashboard/components/PostInquiryCreateModal';
import TabbedSubmitModal from 'dashboard/components/TabbedSubmitModal';
import { SendInquiryEmailFormData } from 'dashboard/forms/FormSendInquiryEmail';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { Account } from 'dashboard/models/account';
import { AccountTypesFilterOption } from 'dashboard/models/account-type';
import { Environment } from 'dashboard/models/environment';
import { Inquiry } from 'dashboard/models/inquiry';
import { InquiryTemplateFilterOption } from 'dashboard/models/inquiry-template';
import { Organization } from 'dashboard/models/organization';
import { TemplateFilterOption } from 'dashboard/models/template';
import { Theme } from 'dashboard/models/theme';
import { showMessage } from 'dashboard/redux/actions/messages';
import { sendInquiryEmail } from 'dashboard/redux/thunk-actions/inquiry';
import { FieldSchema } from 'lib/fields';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import usePermission from 'lib/hooks/usePermission';
import { ApiModel } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';
import { useFormSecurely } from 'lib/security/reactHookForm';

interface CreateInquiryWizardProps {
  templateFilterOptions: (TemplateFilterOption | InquiryTemplateFilterOption)[];
  accountTypeBasics: AccountTypesFilterOption[];
  account: Account | null;
  organization: Organization;
  currentEnvironment: Environment;
  themes: Theme[];
  emailAddressPrefill?: string;
  hideCreateInquiryWizard: () => void;
  onInquiryCreate?: (inquiry: Inquiry) => void;
  isOpen: boolean;
  initialValues: UseCreateInquiryProps;
}

const CreateInquiryWizard = ({
  templateFilterOptions,
  accountTypeBasics,
  account,
  organization,
  currentEnvironment,
  themes,
  emailAddressPrefill,
  hideCreateInquiryWizard,
  onInquiryCreate,
  isOpen,
  initialValues,
}: CreateInquiryWizardProps) => {
  const dispatch = useDispatch();
  const hasITVViewPermission = usePermission(Permission.InquiryTemplateView);
  const { handleCreateInquiry } = useCreateInquiry({ environment: currentEnvironment });
  const { handleLoadInquiryTemplateVersion, loadingInquiryTemplateVersion } =
    useLoadInquiryTemplateVersion();

  const [createdInquiry, setCreatedInquiry] = useState<Inquiry | null>(null);
  const [sendCreatedInquiryEmailSelected, setSendCreatedInquiryEmailSelected] = useState(false);

  const { handleSubmit, control, watch } = useFormSecurely<FieldValues>({
    mode: 'onChange',
    defaultValues: initialValues,
  });
  const selectedTemplateId = watch('templateId');
  const { isSubmitting } = useFormState({
    control,
  });

  const selectedTemplate: TemplateFilterOption | InquiryTemplateFilterOption | undefined =
    templateFilterOptions.find(({ id }) => id === selectedTemplateId);

  const [inquiryTemplateIdToFieldSchema, setInquiryTemplateIdToFieldSchema] = useState<
    Record<string, FieldSchema[] | undefined>
  >({});

  const handleCreateInquiryAndSetCreated = useCallback(
    async (args) => {
      const { sendEmail, ...createArgs } = args;
      const inquiry = await handleCreateInquiry({
        ...createArgs,
        accountId: account?.id,
      });
      if (inquiry) {
        // Will be null if creation failed
        setCreatedInquiry(inquiry);
        setSendCreatedInquiryEmailSelected(sendEmail);
        onInquiryCreate && onInquiryCreate(inquiry);
      }
    },
    [handleCreateInquiry, account?.id, onInquiryCreate],
  );

  useEffect(() => {
    if (
      // Skip if lacking ITV view permission, or no selected template, or
      // legacy template selected, or no published version or if the field
      // schema has already previously been fetched and cached
      !hasITVViewPermission ||
      selectedTemplate == null ||
      selectedTemplate.type !== ApiModel.InquiryTemplate ||
      selectedTemplate['published-version-id'] == null ||
      selectedTemplate.id in inquiryTemplateIdToFieldSchema
    ) {
      return;
    }

    handleLoadInquiryTemplateVersion(selectedTemplate['published-version-id']).then(
      (inquiryTemplateVersion) =>
        setInquiryTemplateIdToFieldSchema((value) => ({
          ...value,
          // Safe access just in case we fail to return an ITV, though this call is not expected to fail
          // given that we call only if we have requisite permissions
          [selectedTemplate.id]: inquiryTemplateVersion?.attributes?.['field-schema'],
        })),
    );
  }, [
    selectedTemplate,
    setInquiryTemplateIdToFieldSchema,
    inquiryTemplateIdToFieldSchema,
    handleLoadInquiryTemplateVersion,
    hasITVViewPermission,
  ]);

  const handlePostInquiryCreateModalClose = () => {
    hideCreateInquiryWizard();
    setCreatedInquiry(null);
    setSendCreatedInquiryEmailSelected(false);
  };

  const { error: handleSendInquiryEmailError, callback: handleSendInquiryEmail } = useApiCallback(
    async (values: SendInquiryEmailFormData) => {
      if (createdInquiry) {
        await sendInquiryEmail(createdInquiry.id, values)();
        setSendCreatedInquiryEmailSelected(false);
        dispatch(
          showMessage(`Successfully sent inquiry link to email recipient`, MessageType.Success),
        );
      } else {
        dispatch(
          showMessage(
            'Could not send inquiry link to email recipient as inquiry was not successfully created',
            MessageType.Failure,
          ),
        );
      }
    },
    [createdInquiry],
  );

  useApiErrorHandler({
    error: handleSendInquiryEmailError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        dispatch(showMessage(permissionsErrorMessage(error), MessageType.Failure));
      } else {
        dispatch(showMessage('Failed to send email.', MessageType.Failure));
      }
    },
    onNetworkError: () =>
      dispatch(showMessage('We are experiencing network issues.', MessageType.Failure)),
  });

  return (
    <ModalWrapper isOpen={isOpen} handleCloseModal={hideCreateInquiryWizard}>
      {/*These two modals are in the same ModalWrapper so that there is no flicker
       as we close the former and open the latter*/}
      {!createdInquiry && (
        <TabbedSubmitModal
          headerText='Create an Inquiry'
          submitText='Create Inquiry'
          tabContents={[
            {
              name: 'General',
              contents: account ? (
                <CreateInquiryGeneralInfoForAccount
                  templateFilterOptions={templateFilterOptions}
                  themes={themes}
                  selectedTemplate={selectedTemplate}
                  submitting={isSubmitting}
                  control={control}
                />
              ) : (
                <CreateInquiryGeneralInfo
                  templateFilterOptions={templateFilterOptions}
                  accountTypeBasics={accountTypeBasics}
                  themes={themes}
                  selectedTemplate={selectedTemplate}
                  submitting={isSubmitting}
                  control={control}
                />
              ),
            },
            {
              name: 'Fields (optional)',
              contents: (
                <CreateInquiryFieldsPrefill
                  fieldSchema={
                    selectedTemplate && inquiryTemplateIdToFieldSchema[selectedTemplate.id]
                  }
                  nullFieldSchemaMessage={
                    hasITVViewPermission
                      ? 'Prefill not supported.'
                      : 'Prefill not supported because you are missing Inquiry Template View permissions.'
                  }
                  isLoading={loadingInquiryTemplateVersion}
                  submitting={isSubmitting}
                  control={control}
                />
              ),
            },
          ]}
          disableSubmit={templateFilterOptions.length === 0}
          isSubmitting={isSubmitting}
          handleSubmit={handleSubmit(handleCreateInquiryAndSetCreated)}
          handleCancel={hideCreateInquiryWizard}
        />
      )}
      {createdInquiry && (
        <PostInquiryCreateModal
          createdInquiry={createdInquiry}
          subdomain={organization.attributes.subdomain}
          customDomain={organization.attributes['inquiry-hosted-flow-custom-domain']}
          emailAddressPrefill={emailAddressPrefill}
          sendCreatedInquiryEmailSelected={sendCreatedInquiryEmailSelected}
          currentEnvironment={currentEnvironment}
          handleSendInquiryEmail={handleSendInquiryEmail}
          handleClose={handlePostInquiryCreateModalClose}
        />
      )}
    </ModalWrapper>
  );
};

export default CreateInquiryWizard;
